import '../../engine/gltf';
import type { CharacterModel } from '@archon3d/core';
import type { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

/** One placed copy of a character model. */
export interface CharacterInstance {
  root: TransformNode;
  meshes: AbstractMesh[];
  animations: Map<string, AnimationGroup>;
  /** Plays a clip by game action (`idle`, `attack`, …) if the model has it. */
  play(
    action: keyof NonNullable<CharacterModel['clips']>,
    loop?: boolean,
  ): AnimationGroup | undefined;
  dispose(): void;
}

/** Models are built 1.8 m tall by the asset pipeline (tools/assets). */
const BUILT_HEIGHT = 1.8;

/** Clips that play while something else is already moving the character. */
const LOCOMOTION: readonly (keyof NonNullable<CharacterModel['clips']>)[] = [
  'walk',
  'run',
  'fly',
  'jump',
];

/** The bone a skeleton is rooted at; Mixamo always calls it `mixamorig:Hips`. */
const ROOT_BONE = /(^|:)(hips|root)$/i;

/**
 * Drops the root bone's translation from a locomotion clip.
 *
 * Mixamo ships walk and run clips with root motion: the hips travel forward over the loop, so a
 * character animated from the clip alone walks under its own power. That is exactly right for a
 * clip you drive a character with, and exactly wrong here, because the board already tweens the
 * piece from square to square. Played together the two add up — the body runs ahead of its square
 * and is yanked back every time the clip loops, which reads as an overshoot and a flicker.
 *
 * The file is untouched and still correct; only our use of it changes. Only locomotion clips are
 * treated this way: an attack that lunges or a death that falls is meant to move the body, and
 * nothing else is moving it at the time.
 */
export function stripRootMotion(group: AnimationGroup): void {
  for (const targeted of [...group.targetedAnimations]) {
    if (targeted.animation.targetProperty !== 'position') continue;
    const target = targeted.target as { name?: string } | null;
    if (!ROOT_BONE.test(target?.name ?? '')) continue;
    group.removeTargetedAnimation(targeted.animation);
  }
}

/**
 * The file a piece on the board shows: the light board model where the character has one
 * (`tools/assets/boardModel.mjs`), for a piece a few centimetres tall; the duel's model otherwise.
 */
export function boardModelFile(model: CharacterModel): string {
  return model.boardFile ?? model.file;
}

/**
 * Loads each GLB once and hands out instances. Static meshes are instanced on the GPU; skinned
 * meshes are cloned with their own skeleton so every piece can animate independently. An
 * instance asked for with `ownMaterials` has meshes and materials of its own.
 */
export class CharacterModelCache {
  private readonly containers = new Map<string, Promise<AssetContainer>>();
  private readonly fetched = new Set<string>();
  private serial = 0;

  constructor(private readonly scene: Scene) {}

  load(file: string): Promise<AssetContainer> {
    let pending = this.containers.get(file);
    if (!pending) {
      const loading = LoadAssetContainerAsync(file, this.scene);
      this.containers.set(file, loading);
      // A failed load is forgotten: one dropped connection must not leave the character a
      // stand-in for the rest of the visit, so the next piece that needs it tries again.
      loading.catch(() => {
        if (this.containers.get(file) === loading) this.containers.delete(file);
      });
      pending = loading;
    }
    return pending;
  }

  /**
   * Fetches a file into the browser's cache without building it, so a later `load` has only to
   * read it: the board shows light models, and a duel's full ones are ready when a fight starts.
   * It waits for the loads under way, so what is on screen comes first.
   */
  prefetch(file: string): void {
    if (this.containers.has(file) || this.fetched.has(file)) return;
    this.fetched.add(file);
    void Promise.allSettled([...this.containers.values()])
      .then(() => fetch(file))
      .then((response) => response.arrayBuffer())
      .catch(() => {});
  }

  /**
   * `height` is the model's target height in metres, its board size by default; `file` is the
   * duel's model by default (`boardModelFile` gives the board's).
   *
   * `ownMaterials` is for a copy that changes its own look: a duel's fighter, whose hit flashes,
   * burns and fades, when the other fighter may be the same character (client-arena:bugs#7). It
   * gets copies of the materials, and meshes of its own rather than GPU instances, which draw with
   * their source's material and visibility. The copies' textures are the file's own on the GPU,
   * found in the engine's cache by their address; only two fighters of the same static model cost
   * a draw call more, drawn apart. The board keeps its pieces instanced.
   */
  async instantiate(
    model: CharacterModel,
    name: string,
    {
      height = model.boardHeight ?? 0.09,
      file = model.file,
      ownMaterials = false,
    }: { height?: number; file?: string; ownMaterials?: boolean } = {},
  ): Promise<CharacterInstance> {
    const container = await this.load(file);
    const entries = container.instantiateModelsToScene(
      (sourceName) => `${name}:${sourceName}`,
      ownMaterials,
      { doNotInstantiate: ownMaterials },
    );
    const root = new TransformNode(name, this.scene);
    for (const node of entries.rootNodes) node.parent = root;
    const scale = height / BUILT_HEIGHT;
    root.scaling.setAll(scale);
    root.rotation.y = ((model.yaw ?? 0) * Math.PI) / 180;

    const meshes = root.getChildMeshes(false);
    const animations = new Map<string, AnimationGroup>();
    for (const group of entries.animationGroups) {
      group.stop();
      animations.set(group.name.replace(`${name}:`, ''), group);
    }
    for (const action of LOCOMOTION) {
      const clip = model.clips?.[action];
      const group = clip ? animations.get(clip) : undefined;
      if (group) stripRootMotion(group);
    }
    const serial = this.serial++;

    return {
      root,
      meshes,
      animations,
      play: (action, loop = true) => {
        const clip = model.clips?.[action];
        const group = clip ? animations.get(clip) : undefined;
        if (!group) return undefined;
        for (const other of animations.values()) if (other !== group) other.stop();
        group.start(loop, 1, group.from, group.to, false);
        // Desynchronise identical pieces so a row of knights does not breathe in lockstep. A
        // travelling piece is exempt: jumping its clip to an arbitrary frame jolts the body, and
        // no two pieces walk at the same moment anyway.
        if (loop && !LOCOMOTION.includes(action))
          group.goToFrame(group.from + ((serial * 7) % Math.max(1, group.to - group.from)));
        return group;
      },
      dispose: () => {
        const materials = ownMaterials
          ? new Set(meshes.flatMap((mesh) => (mesh.material ? [mesh.material] : [])))
          : [];
        for (const group of animations.values()) group.dispose();
        root.dispose(false, false);
        // Each skinned instance has its own skeleton, with its bones' texture on the GPU.
        for (const skeleton of entries.skeletons) skeleton.dispose();
        // Its own materials go with it. Their textures only let go of the file's: the GPU keeps
        // them while the file's own, or another copy's, still hold them.
        for (const material of materials) material.dispose(false, true);
      },
    };
  }

  dispose(): void {
    // A navigation/React development remount can dispose the scene while a GLB is in flight.
    // Consumers still receive real load failures; cleanup must not create a second unhandled rejection.
    for (const pending of this.containers.values())
      void pending.then(
        (c) => c.dispose(),
        () => {},
      );
    this.containers.clear();
  }
}
